import { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import SectionDivider from 'components/pages/Planner/SectionDivider';
import { DIRECT_SALES_SWEEP, DirectSalesSweep } from 'components/common/types/DirectSalesCampaign.types';
import { Store } from 'components/common/types/Store.types';
import { CodeNameModel } from 'components/common/types';
import { hideSecondaryPanel } from 'store/productManagement/reducer';
import { SelectionPermission } from 'store/productManagement/reducer.types';
import { InputAlign } from 'lib/BaseInput';
import InputNumber from 'lib/InputNumber';
import SelectOption from 'lib/SelectOption';
import { isValueRequired } from '../../utils/isValueRequired';
import { definedRangePermissionOptions, permissionOptions } from '../../consts';
import { secondaryPanelLayoutStyles } from '../../styles';
import SelectionPermissionDropdown from '../SelectionPermissionDropdown/SelectionPermissionDropdown';
import { ProductShareOfTimePanelProps, ProductSweepType } from './ProductShareOfTime.types';
import { isConfirmDisabled, getCorrectedSot } from './utils';
import { ProductSecondaryPanel } from '../ProductSecondaryPanel/ProductSecondaryPanel';

const ProductShareOfTimePanel: React.FC<ProductShareOfTimePanelProps> = ({ onConfirm, isReadOnly = false }) => {
  const dispatch = useDispatch();

  const defaultSot = useSelector((state: Store) => state.productManagement.newProduct.sot);
  const defaultSweep = useSelector((state: Store) => state.productManagement.newProduct.sweep);

  const defaultSotSelectedPermission =
    definedRangePermissionOptions.find(({ code }) => code === defaultSot?.selectionPermission) ??
    definedRangePermissionOptions[0];
  const defaultSweepSelectedPermission =
    permissionOptions.find(({ code }) => code === defaultSweep?.selectionPermission) ?? permissionOptions[0];

  const [sotSelectedPermission, setSotSelectedPermission] = useState<CodeNameModel>(defaultSotSelectedPermission);
  const [sweepSelectedPermission, setSweepSelectedPermission] = useState<CodeNameModel>(defaultSweepSelectedPermission);

  const [sweepValue, setSweepValue] = useState<ProductSweepType | undefined>(defaultSweep?.value);

  const [defaultSotValue, setDefaultSotValue] = useState<number | undefined>(
    defaultSot?.value?.defaultValue ?? undefined,
  );
  const [minSotValue, setMinSotValue] = useState<number | undefined>(defaultSot?.value?.minValue);
  const [maxSotValue, setMaxSotValue] = useState<number | undefined>(defaultSot?.value?.maxValue);

  const [defaultDraft, setDefaultDraft] = useState<number | undefined>(defaultSotValue);
  const [minDraft, setMinDraft] = useState<number | undefined>(minSotValue);
  const [maxDraft, setMaxDraft] = useState<number | undefined>(maxSotValue);

  const showSotRange = sotSelectedPermission.code === SelectionPermission.RANGE_VALUE;

  const isDisabled =
    isReadOnly ||
    isConfirmDisabled({
      sotSelectedPermission,
      sweepSelectedPermission,
      defaultSotValue,
      minSotValue,
      maxSotValue,
      sweepValue,
    });

  const handleSotBlur = (
    value: number | undefined,
    min: number | undefined,
    max: number | undefined,
    setValue: (value: number | undefined) => void,
    setDraft: (value: number | undefined) => void,
  ): void => {
    const corrected = getCorrectedSot(value, min, max, sotSelectedPermission.code === SelectionPermission.FIXED_VALUE);
    setValue(corrected);
    setDraft(corrected);
  };

  useEffect(() => {
    if (defaultSotValue === undefined) return;

    const correctedDefault = getCorrectedSot(defaultDraft, minSotValue, maxSotValue, false);
    if (correctedDefault !== defaultDraft) {
      setDefaultSotValue(correctedDefault);
      setDefaultDraft(correctedDefault);
    }
  }, [minSotValue, maxSotValue]);

  return (
    <ProductSecondaryPanel
      size="lg"
      title="Share of time / Sweep"
      onSubmit={() =>
        onConfirm({
          sotValue: {
            defaultValue: defaultSotValue,
            minValue: minSotValue,
            maxValue: maxSotValue,
          },
          sotSelectedPermission,
          sweepValue,
          sweepSelectedPermission,
        })
      }
      onCancel={() => dispatch(hideSecondaryPanel())}
      isDisabled={isDisabled}
      customStyles={secondaryPanelLayoutStyles}
    >
      <SelectionPermissionDropdown
        label="Share of time selection permissions"
        selectedPermission={sotSelectedPermission}
        setSelectedPermission={setSotSelectedPermission}
        permissionOptions={definedRangePermissionOptions}
      />
      {(isValueRequired(sotSelectedPermission.code) || showSotRange) && (
        <div className="mb-2 mt-4">
          <InputNumber
            value={defaultDraft}
            onChange={(value?: number) => setDefaultDraft(value)}
            onBlur={() => handleSotBlur(defaultDraft, minSotValue, maxSotValue, setDefaultSotValue, setDefaultDraft)}
            id="sot"
            dataTestId="direct-sales-panel-sot"
            label="Default"
            textSymbol="%"
            min={0}
            max={100}
            isEmpty
            align={InputAlign.RIGHT}
          />
        </div>
      )}
      {showSotRange && (
        <>
          <div className="mb-2">
            <InputNumber
              value={minDraft}
              onChange={(value?: number) => setMinDraft(value)}
              onBlur={() => handleSotBlur(minDraft, 0, maxSotValue, setMinSotValue, setMinDraft)}
              id="sot"
              dataTestId="direct-sales-panel-sot"
              label="Minimum"
              textSymbol="%"
              min={0}
              max={maxSotValue ?? 100}
              isEmpty
              align={InputAlign.RIGHT}
            />
          </div>
          <div className="mb-4">
            <InputNumber
              value={maxDraft}
              onChange={(value?: number) => setMaxDraft(value)}
              onBlur={() => handleSotBlur(maxDraft, minSotValue, 100, setMaxSotValue, setMaxDraft)}
              id="sot"
              dataTestId="direct-sales-panel-sot"
              label="Maximum"
              textSymbol="%"
              min={minSotValue ?? 0}
              max={100}
              isEmpty
              align={InputAlign.RIGHT}
            />
          </div>
        </>
      )}
      <div className="my-4">
        <SectionDivider />
      </div>
      <SelectionPermissionDropdown
        label="Sweep selection permissions"
        selectedPermission={sweepSelectedPermission}
        setSelectedPermission={setSweepSelectedPermission}
        permissionOptions={permissionOptions}
      />
      {isValueRequired(sweepSelectedPermission.code) && (
        <div className="my-5">
          <p className="body-sm text-secondary">Choose one sweep type</p>
          {Object.entries(DIRECT_SALES_SWEEP)
            .filter(([key]) => key !== DirectSalesSweep.NONE)
            .map(([key, { label, description }]) => (
              <SelectOption
                key={key}
                hasRadio
                isSelected={sweepValue === key}
                onClick={() => setSweepValue(sweepValue === key ? undefined : key)}
                value={{ code: key, name: label }}
              >
                <div className="w-72">
                  <p>{label}</p>
                  <p className="body-sm text-secondary whitespace-normal leading-tight">{description}</p>
                </div>
              </SelectOption>
            ))}
        </div>
      )}
    </ProductSecondaryPanel>
  );
};

export default ProductShareOfTimePanel;
